import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import { DeleteCircle } from '@ringcentral/juno-icon';
import { clearReactReferencesInNode, combineClasses, combineProps, styled, useControlled, useDeprecatedCheck, useEventCallback, useForkRef, useThemeProps, useId, } from '../../../foundation';
import { useRcFormContext } from '../Form/Form/FormContext';
import { ClearIconButton, TextFieldStyle } from './styles';
import { RcOutlineTextFieldIconSizes, RcOutlineTextFieldInputClasses, RcTextFieldClasses, RcTextFieldFormHelperTextClasses, RcTextFieldInputClasses, RcTextFieldInputLabelClasses, } from './utils';
var combineOutlineClasses = combineClasses(RcTextFieldInputClasses, RcOutlineTextFieldInputClasses);
var displayName = 'RcTextField';
var _RcTextField = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcTextField' });
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDeprecatedCheck(RcTextField, props, [
            {
                prop: 'focusOnMount',
                time: '2021-4',
                comment: "please use `autoFocus` to replace that",
            },
            {
                prop: 'selectOnMount',
                time: '2021-4',
                comment: "please use `autoSelect` to replace that",
            },
            {
                prop: 'clearLabel',
                time: '2021-4',
                comment: "clear button label, please use `clearButtonProps` with `title` replace that",
            },
            {
                prop: 'clearAriaLabel',
                time: '2021-4',
                comment: "clear button aria-label, use `clearButtonProps` with `aria-label` replace that",
            },
        ]);
    }
    var classesProp = props.classes, InputLabelPropsProp = props.InputLabelProps, FormHelperTextPropsProp = props.FormHelperTextProps, valueProp = props.value, focusOnMount = props.focusOnMount, selectOnMount = props.selectOnMount, clearAriaLabel = props.clearAriaLabel, 
    // TODO: remove when remove focusOnMount
    _a = props.autoFocus, 
    // TODO: remove when remove focusOnMount
    autoFocusProp = _a === void 0 ? focusOnMount : _a, InputPropsProp = props.InputProps, error = props.error, helperText = props.helperText, 
    // no need assign to MuiTextField
    defaultValue = props.defaultValue, inputRefProp = props.inputRef, _b = props.autoSelect, autoSelect = _b === void 0 ? selectOnMount : _b, autoFocusDelay = props.autoFocusDelay, clearLabel = props.clearLabel, clearButtonProps = props.clearButtonProps, clearBtn = props.clearBtn, validate = props.validate, onChange = props.onChange, onBlur = props.onBlur, onFocus = props.onFocus, onClear = props.onClear, idProp = props.id, 
    // #region outline pick props
    variant = props.variant, radius = props.radius, size = props.size, align = props.align, textVariant = props.textVariant, gutterBottom = props.gutterBottom, color = props.color, 
    // #endregion
    rest = __rest(props, ["classes", "InputLabelProps", "FormHelperTextProps", "value", "focusOnMount", "selectOnMount", "clearAriaLabel", "autoFocus", "InputProps", "error", "helperText", "defaultValue", "inputRef", "autoSelect", "autoFocusDelay", "clearLabel", "clearButtonProps", "clearBtn", "validate", "onChange", "onBlur", "onFocus", "onClear", "id", "variant", "radius", "size", "align", "textVariant", "gutterBottom", "color"]);
    var formContext = useRcFormContext();
    var innerRef = useRef();
    var inputRef = useForkRef(inputRefProp, innerRef);
    var _c = __read(useState(''), 2), validateMessage = _c[0], setValidateMessage = _c[1];
    var runValidate = useEventCallback(function (value) {
        if (value === void 0) { value = valueProp; }
        var validateMessage = false;
        if (validate) {
            validateMessage = validate(value);
            setValidateMessage(validateMessage !== false ? validateMessage : '');
        }
        return validateMessage;
    });
    var handleClear = useEventCallback(function (event) {
        var fakeEvent = { target: { value: '' } };
        onClear === null || onClear === void 0 ? void 0 : onClear(event);
        onChange === null || onChange === void 0 ? void 0 : onChange(fakeEvent);
        setValidateMessage('');
    });
    var endAdornment = (function () {
        var getCleanButton = function () {
            var iconTitle = clearLabel || (clearButtonProps === null || clearButtonProps === void 0 ? void 0 : clearButtonProps.title);
            var combineClearProps = combineProps({
                TooltipProps: {
                    tooltipForceHide: !iconTitle,
                },
                onClick: handleClear,
                size: RcOutlineTextFieldIconSizes[size],
            }, clearButtonProps);
            return (React.createElement(ClearIconButton, __assign({ title: iconTitle, "aria-label": clearAriaLabel || iconTitle, symbol: DeleteCircle }, combineClearProps)));
        };
        if (InputPropsProp === null || InputPropsProp === void 0 ? void 0 : InputPropsProp.endAdornment) {
            if (clearBtn) {
                return (React.createElement(React.Fragment, null,
                    getCleanButton(),
                    InputPropsProp.endAdornment));
            }
            return InputPropsProp.endAdornment;
        }
        return clearBtn ? getCleanButton() : undefined;
    })();
    var events = combineProps({
        onBlur: function () {
            runValidate();
        },
        onFocus: function () {
            if (validateMessage !== '') {
                setValidateMessage('');
            }
        },
    }, {
        onBlur: onBlur,
        onFocus: onFocus,
    });
    var classes = useMemo(function () { return combineClasses(RcTextFieldClasses, classesProp); }, [classesProp]);
    var additionClasses = {
        outline: combineOutlineClasses,
        borderLess: undefined,
        standard: undefined,
    }[variant];
    var InputProps = combineProps({
        classes: additionClasses || RcTextFieldInputClasses,
        disableUnderline: ['outline', 'borderLess', 'inline'].includes(variant),
    }, __assign(__assign({}, InputPropsProp), { endAdornment: endAdornment }));
    var FormHelperTextProps = combineProps({ classes: RcTextFieldFormHelperTextClasses }, FormHelperTextPropsProp);
    var InputLabelProps = combineProps({ classes: RcTextFieldInputLabelClasses, shrink: true }, InputLabelPropsProp);
    useLayoutEffect(function () {
        var inputElm = innerRef.current;
        var autoFocus = autoFocusProp || (InputPropsProp === null || InputPropsProp === void 0 ? void 0 : InputPropsProp.autoFocus);
        var timerId = null;
        if (autoSelect && (inputElm === null || inputElm === void 0 ? void 0 : inputElm.value)) {
            inputElm.select();
        }
        else {
            var focus_1 = function () { return inputElm === null || inputElm === void 0 ? void 0 : inputElm.focus(); };
            if (autoFocus) {
                if (autoFocusDelay) {
                    timerId = setTimeout(focus_1, autoFocusDelay);
                }
                else {
                    focus_1();
                }
            }
        }
        return function () {
            if (timerId)
                clearTimeout(timerId);
            if (inputElm) {
                clearReactReferencesInNode(inputElm);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // TODO: remove this after upgrade to Mui5
    var id = useId(idProp);
    useEffect(function () {
        if (id)
            formContext.set(id, { validate: function () { return runValidate(); } });
        return function () {
            if (id)
                formContext.delete(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(MuiTextField, __assign({}, rest, events, { id: id, value: valueProp, onChange: onChange, error: validate ? !!validateMessage : error, helperText: validateMessage || helperText, classes: classes, FormHelperTextProps: FormHelperTextProps, InputLabelProps: InputLabelProps, InputProps: InputProps, inputRef: inputRef, ref: ref })));
});
/** @release */
var RcTextField = styled(_RcTextField).attrs(function (_a) {
    var _b = _a.defaultValue, defaultValue = _b === void 0 ? '' : _b, valueProp = _a.value, onChangeProp = _a.onChange, rest = __rest(_a, ["defaultValue", "value", "onChange"]);
    var _c = __read(useControlled({
        default: defaultValue,
        controlled: valueProp,
        name: displayName,
    }), 2), value = _c[0], setValue = _c[1];
    var onChange = function (event) {
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(event);
        setValue(event.target.value);
    };
    return __assign(__assign({}, rest), { value: value, onChange: onChange });
})(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), TextFieldStyle);
RcTextField.defaultProps = {
    clearBtn: true,
    variant: 'standard',
    radius: 'lg',
    size: 'medium',
    textVariant: 'subheading1',
};
RcTextField.displayName = displayName;
export { RcOutlineTextFieldIconSizes, RcTextField };
var templateObject_1;
